import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import axios from "axios"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Textarea } from "design-system/atoms/Textarea"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Route as ReplaceVaultEntryRoute } from "routes/_base/_auth/settings/keys-and-secrets/vault.$key.replace"
import { ReplaceVaultEntrySchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { useVaultEntriesQuery } from "../../hooks/useVaultEntriesQuery"
import { maskedInputClassName, noAutofillProps } from "../../lib/vaultInput"
import { vaultQuery } from "../../lib/vaultQuery"

type FieldValues = z.infer<typeof ReplaceVaultEntrySchema>

export function ReplaceVaultEntry() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { key } = ReplaceVaultEntryRoute.useParams()
  const { data: entries } = useVaultEntriesQuery()

  const entry = entries?.find((item) => item.key === key)
  const isSecret = entry?.isSecret !== false

  const form = useForm<FieldValues>({
    resolver: zodResolver(ReplaceVaultEntrySchema),
    defaultValues: { value: isSecret ? "" : (entry?.value ?? "") },
  })

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ value }: FieldValues) => {
      await axios.put(`/vault/${encodeURIComponent(key)}`, { value })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: vaultQuery().queryKey })
      toast.success(
        t(
          isSecret
            ? "settings.keysAndSecrets.vault.replaced"
            : "settings.keysAndSecrets.vault.edited",
        ),
      )
      onClose()
    },
  })

  function onClose() {
    navigate({ to: "/settings/keys-and-secrets" })
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[475px]">
        <DialogHeader>
          <DialogTitle>
            {t(
              isSecret
                ? "settings.keysAndSecrets.vault.replace"
                : "settings.keysAndSecrets.vault.edit",
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              isSecret
                ? "settings.keysAndSecrets.vault.replaceDescription"
                : "settings.keysAndSecrets.vault.editDescription",
              { key },
            )}
          </DialogDescription>
        </DialogHeader>
        <Form
          {...form}
          errorTranslations={getErrorTranslations()}
          onSubmit={form.handleSubmit((values) => mutate(values))}
        >
          <FormField
            control={form.control}
            name="value"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t(
                    isSecret
                      ? "settings.keysAndSecrets.vault.newValue"
                      : "settings.keysAndSecrets.vault.value",
                  )}
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    {...noAutofillProps}
                    autoFocus
                    className={isSecret ? maskedInputClassName : undefined}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t("shared.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {t("settings.keysAndSecrets.vault.save")}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
